import { gql, useQuery } from "@apollo/client";
import { TenantPermission, useTenantPermissions } from "features/tenant";
import React, { useMemo } from "react";
import ApiKeyAuthSelect, { ApiKeyAuthProps } from "./ApiKeyAuthSelect";
import { QueryApiKeyValues } from "./schema/QueryApiKeyValues";

export const apiKeyValuesQuery = gql`
  query QueryApiKeyValues {
    publicApi {
      apiKeys {
        apiKeys {
          id
          name
          value
          enabled
        }
      }
    }
  }
`;

export type SwaggerPlugin = () => {
  wrapComponents: {
    apiKeyAuth: (
      Original: React.ComponentType<ApiKeyAuthProps>
    ) => React.ComponentType<ApiKeyAuthProps>;
  };
};

/**
 * Swagger UI plugin offering the tenant's API keys in the authorize dialog, if the user may read them.
 * swagger-ui reads plugins only on mount, so render it once `loading` is false.
 */
export function useApiKeySelectionPlugin() {
  const permissions = useTenantPermissions();
  const canReadApiKeys =
    !!permissions.data?.tenant.access.me?.permissions.includes(
      TenantPermission.ReadApiKeys
    );
  const apiKeysQuery = useQuery<QueryApiKeyValues>(apiKeyValuesQuery, {
    // keeps the secret key values out of the Apollo cache
    fetchPolicy: "no-cache",
    skip: !canReadApiKeys
  });
  const apiKeys = apiKeysQuery.data?.publicApi.apiKeys.apiKeys;

  const plugin = useMemo<SwaggerPlugin | undefined>(
    () =>
      apiKeys?.length
        ? () => ({
            wrapComponents: {
              apiKeyAuth: Original => props =>
                (
                  <ApiKeyAuthSelect
                    Original={Original}
                    apiKeys={apiKeys}
                    {...props}
                  />
                )
            }
          })
        : undefined,
    [apiKeys]
  );

  return {
    plugin,
    loading: permissions.loading || apiKeysQuery.loading
  };
}
